import type { Economy } from '@audigoal/core';

type QuestDef = Economy.QuestDef;

/**
 * The daily quest pool (plan doc 13).
 *
 * Three are drawn each day, one easy, one medium, one hard. The three numbers
 * on each are those difficulties; `selectDailyQuests` in core scales them for
 * how far into the course the learner is and decides which three to show.
 *
 * Titles carry `{n}` where the target goes and are written as instructions
 * rather than descriptions: a quest card is a thing to do today.
 */
export const DAILY_QUESTS: QuestDef[] = [
  {
    id: 'quest.xp',
    title: 'Earn {n} XP',
    metric: 'xp',
    targets: [30, 50, 80],
  },
  {
    id: 'quest.lessons',
    title: 'Complete {n} lessons',
    metric: 'lessons',
    targets: [2, 3, 4],
  },
  {
    id: 'quest.combo',
    title: 'Get {n} correct in a row',
    metric: 'comboBest',
    targets: [10, 15, 20],
  },
  {
    id: 'quest.ear',
    title: 'Answer {n} ear-training exercises',
    metric: 'earExercises',
    targets: [10, 15, 20],
  },
  {
    id: 'quest.mic',
    title: 'Pass {n} exercises by playing them',
    metric: 'micPasses',
    targets: [5, 8, 10],
    instrument: true,
  },
  {
    id: 'quest.song',
    title: 'Play {n} song',
    metric: 'songsPlayed',
    targets: [1, 2, 3],
    instrument: true,
  },
  {
    id: 'quest.song-stars',
    title: 'Score two stars on {n} song',
    metric: 'songTwoStars',
    targets: [1, 1, 2],
    instrument: true,
  },
  {
    id: 'quest.mistakes',
    title: 'Practice your mistakes once',
    metric: 'mistakesSessions',
    targets: [1, 1, 1],
  },
  {
    id: 'quest.perfect',
    title: 'Complete {n} lesson with no mistakes',
    metric: 'perfectLessons',
    targets: [1, 1, 2],
  },
  {
    id: 'quest.studio',
    title: 'Spend {n} minutes in the Studio',
    metric: 'studioMinutes',
    targets: [5, 8, 12],
    instrument: true,
  },
  {
    id: 'quest.warm-up',
    title: 'Complete the Daily Warm-up',
    metric: 'warmUps',
    targets: [1, 1, 1],
    instrument: true,
  },
  {
    id: 'quest.second-instrument',
    title: 'Play on your second instrument',
    metric: 'secondInstrument',
    targets: [1, 1, 1],
    instrument: true,
  },
  {
    id: 'quest.legendary',
    title: 'Complete {n} legendary level',
    metric: 'legendary',
    targets: [1, 1, 1],
    minSection: 3,
  },
  {
    id: 'quest.review',
    title: 'Review {n} skills that are due',
    metric: 'skillsReviewed',
    targets: [5, 8, 12],
  },
  {
    id: 'quest.morning',
    title: 'Earn {n} XP before noon',
    metric: 'xpBeforeNoon',
    targets: [30, 50, 80],
  },
];

const BY_ID = new Map(DAILY_QUESTS.map((quest) => [quest.id, quest]));

export function questById(id: string): QuestDef | undefined {
  return BY_ID.get(id);
}

/** The title with its target filled in. */
export function questTitle(quest: QuestDef, target: number): string {
  return quest.title.replace('{n}', String(target));
}

/**
 * Monthly framings for the same pool.
 *
 * A month is thirty times a day, so the copy changes even where the metric does
 * not: "Complete 4 lessons" becomes "Complete 40 lessons this month".
 */
export const MONTHLY_TITLES: Readonly<Record<string, string>> = {
  'quest.xp': 'Earn {n} XP this month',
  'quest.lessons': 'Complete {n} lessons this month',
  'quest.combo': 'Reach a run of {n} correct',
  'quest.ear': 'Answer {n} ear-training exercises',
  'quest.mic': 'Pass {n} exercises by playing them',
  'quest.song': 'Play {n} songs',
  'quest.song-stars': 'Two-star {n} songs',
  'quest.mistakes': 'Practice your mistakes {n} times',
  'quest.perfect': 'Complete {n} flawless lessons',
  'quest.studio': 'Spend {n} minutes in the Studio',
  'quest.warm-up': 'Complete {n} Daily Warm-ups',
  'quest.second-instrument': 'Play {n} sessions on your second instrument',
  'quest.legendary': 'Complete {n} legendary levels',
  'quest.review': 'Review {n} due skills',
  'quest.morning': 'Earn {n} XP before noon',
};
